/**
 * The questions[] builder — the core of a feedback definition.
 *
 * Which sub-fields a question shows is derived from its questionType, and the
 * type list itself comes from the value set. Nothing here enumerates the types:
 * `isChoiceType` etc. inspect the CODE, so a type added through the Value Sets
 * screen renders sensibly without a change to this file.
 */
import { InputSwitch } from 'sharedUi/InputSwitch';
import { Input } from 'sharedUi/Input';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { Message } from 'sharedUi/Message';
import FormField from '../../components/common/form-field';
import NumberStepper from '../../components/common/number-stepper';
import type { FeedbackQuestion, FeedbackQuestionOption } from '../../types/feedback-master.types';
import {
  isChoiceType, isNumberType, isRatingType, isTextType,
  useQuestionTypes, useSignalRoles,
} from './use-feedback-value-sets';

const blankQuestion = (order: number): FeedbackQuestion => ({
  questionCode: '',
  label: '',
  questionType: '',
  required: false,
  displayOrder: order,
  options: [],
  allowComment: false,
});

interface Props {
  value: FeedbackQuestion[];
  onChange: (next: FeedbackQuestion[]) => void;
  /** shown once a save has been attempted */
  submitted?: boolean;
}

const QuestionBuilder = ({ value, onChange, submitted }: Props) => {
  const { options: questionTypeOptions, seeded } = useQuestionTypes();
  const { options: signalRoleOptions } = useSignalRoles();

  const patch = (i: number, p: Partial<FeedbackQuestion>) =>
    onChange(value.map((q, n) => (n === i ? { ...q, ...p } : q)));

  /** Changing the type discards the sub-fields that no longer apply — a stale
   *  ratingScale on a TEXT question is a 422 from the server. */
  const changeType = (i: number, questionType: string) =>
    patch(i, {
      questionType,
      options: isChoiceType(questionType) ? value[i].options ?? [] : [],
      ratingScale: isRatingType(questionType) ? (value[i].ratingScale ?? 5) : null,
      maxLength: isTextType(questionType) ? (value[i].maxLength ?? 2000) : null,
      minValue: isNumberType(questionType) ? value[i].minValue ?? null : null,
      maxValue: isNumberType(questionType) ? value[i].maxValue ?? null : null,
    });

  const move = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next.map((q, n) => ({ ...q, displayOrder: n + 1 })));
  };

  const setOptions = (i: number, options: FeedbackQuestionOption[]) => patch(i, { options });

  return (
    <div className="flex flex-col gap-3">
      {!seeded && (
        <Message severity="warn" className="w-full justify-start"
          text="No question types are available yet. Contact your administrator to configure them before saving." />
      )}

      {value.length === 0 && (
        <div className="rounded-md border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500 dark:border-gray-600">
          A definition needs at least one question — an empty form asks the user nothing.
        </div>
      )}

      {value.map((q, i) => (
        <div key={i} className="rounded-md border border-gray-200 p-3 dark:border-gray-700">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-y-2">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Question {i + 1}
              </span>
              {/* Both switches read as one line of settings for this question, so
                  they belong together at the top rather than scattered through the
                  grid between unrelated text inputs. */}
              <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-300">
                <InputSwitch checked={!!q.required}
                  onChange={(e) => patch(i, { required: !!e.value })} />
                Required
              </label>
              <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-300">
                <InputSwitch checked={!!q.allowComment}
                  onChange={(e) => patch(i, { allowComment: !!e.value })} />
                Allow comment
              </label>
            </div>
            <div className="flex items-center gap-1">
              <Button aria-label="Move up" icon="pi pi-arrow-up" text rounded tooltip="Move up"
                disabled={i === 0} onClick={() => move(i, -1)} />
              <Button aria-label="Move down" icon="pi pi-arrow-down" text rounded tooltip="Move down"
                disabled={i === value.length - 1} onClick={() => move(i, 1)} />
              <Button aria-label="Delete" icon="pi pi-trash" text rounded severity="danger" tooltip="Remove"
                onClick={() => onChange(value.filter((_, n) => n !== i))} />
            </div>
          </div>

          <div className="grid grid-cols-12 items-start gap-x-3 gap-y-3">
            <FormField className="col-span-12 sm:col-span-4" label="Question Code" required
              error={submitted && !q.questionCode.trim() ? 'Required.' : undefined}>
              <Input name={`qc-${i}`} value={q.questionCode} className="w-full"
                placeholder="ANSWER_RATING"
                onChange={(v: string) => patch(i, { questionCode: v.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })} />
            </FormField>

            <FormField className="col-span-12 sm:col-span-5" label="Label" required
              error={submitted && !q.label.trim() ? 'Required.' : undefined}>
              <Input name={`ql-${i}`} value={q.label} className="w-full"
                placeholder="How would you rate this answer?"
                onChange={(v: string) => patch(i, { label: v })} />
            </FormField>

            <FormField className="col-span-12 sm:col-span-3" label="Type" required
              error={submitted && !q.questionType ? 'Required.' : undefined}>
              <Dropdown value={q.questionType} options={questionTypeOptions}
                optionLabel="label" optionValue="value" className="w-full"
                placeholder="Select type"
                onChange={(e: { value: string }) => changeType(i, e.value)} />
            </FormField>

            <FormField className="col-span-12 sm:col-span-6" label="Help text">
              <Input name={`qh-${i}`} value={q.helpText || ''} className="w-full"
                onChange={(v: string) => patch(i, { helpText: v || null })} />
            </FormField>

            <FormField className="col-span-6 sm:col-span-3" label="Signal role"
              info="Marks this question as THE overall score for reports. Pick RATING for a star/number question or SENTIMENT for a good/bad question. Only one question per form may carry each role.">
              <Dropdown value={q.signalRole || ''} options={[{ label: 'None', value: '' }, ...signalRoleOptions]}
                optionLabel="label" optionValue="value" className="w-full"
                onChange={(e: { value: string }) => patch(i, { signalRole: e.value || null })} />
            </FormField>

            {/* ---- type-specific ---- */}
            {isRatingType(q.questionType) && (
              <FormField className="col-span-6 sm:col-span-3" label="Rating scale">
                <NumberStepper value={q.ratingScale ?? 5} min={2} max={10}
                  ariaLabel="Rating scale"
                  onChange={(v) => patch(i, { ratingScale: v ?? 5 })} />
              </FormField>
            )}

            {isTextType(q.questionType) && (
              <FormField className="col-span-6 sm:col-span-3" label="Max length">
                <NumberStepper value={q.maxLength ?? 2000} min={1} max={20000} step={100}
                  ariaLabel="Max length"
                  onChange={(v) => patch(i, { maxLength: v })} />
              </FormField>
            )}

            {isNumberType(q.questionType) && (
              <>
                <FormField className="col-span-6 sm:col-span-3" label="Min value">
                  <NumberStepper value={q.minValue ?? null} ariaLabel="Min value"
                    onChange={(v) => patch(i, { minValue: v })} />
                </FormField>
                <FormField className="col-span-6 sm:col-span-3" label="Max value">
                  <NumberStepper value={q.maxValue ?? null} ariaLabel="Max value"
                    onChange={(v) => patch(i, { maxValue: v })} />
                </FormField>
              </>
            )}

            {isChoiceType(q.questionType) && (
              <div className="col-span-12 rounded-md border border-gray-200 p-3 dark:border-gray-700">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Options
                  </span>
                  <div className="flex items-center gap-2">
                    <Input name={`qsrc-${i}`} value={q.optionsSource || ''}
                      placeholder="or a value-set key…" className="w-56"
                      onChange={(v: string) => patch(i, { optionsSource: v || null })} />
                    <Button label="Add option" icon="pi pi-plus" text
                      onClick={() => setOptions(i, [...(q.options || []),
                        { value: '', label: '', displayOrder: (q.options?.length ?? 0) + 1, isActive: true }])} />
                  </div>
                </div>

                {!q.options?.length && !q.optionsSource && (
                  <div className="text-xs text-amber-600 dark:text-amber-400">
                    A choice question needs options, or a value-set key to source them from.
                  </div>
                )}

                {(q.options || []).map((o, oi) => (
                  <div key={oi} className="mb-2 flex items-center gap-2">
                    <Input name={`ov-${i}-${oi}`} value={o.value} className="w-40"
                      placeholder="VALUE"
                      onChange={(v: string) => setOptions(i, (q.options || []).map((x, n) =>
                        (n === oi ? { ...x, value: v.toUpperCase().replace(/[^A-Z0-9_]/g, '_') } : x)))} />
                    <Input name={`ol-${i}-${oi}`} value={o.label} className="flex-1"
                      placeholder="What the user reads"
                      onChange={(v: string) => setOptions(i, (q.options || []).map((x, n) =>
                        (n === oi ? { ...x, label: v } : x)))} />
                    <span className="flex items-center gap-1 text-xs text-gray-500" title="Retire without breaking old answers">
                      <InputSwitch checked={o.isActive !== false}
                        onChange={(e) => setOptions(i, (q.options || []).map((x, n) =>
                          (n === oi ? { ...x, isActive: !!e.value } : x)))} />
                      active
                    </span>
                    <Button icon="pi pi-times" text rounded severity="danger" aria-label="Remove option"
                      onClick={() => setOptions(i, (q.options || []).filter((_, n) => n !== oi))} />
                  </div>
                ))}
              </div>
            )}

            {/* ---- conditional display ---- */}
            <FormField className="col-span-12 sm:col-span-6" label="Show only when">
              <Dropdown
                value={q.visibleWhen?.questionCode || ''}
                options={[{ label: 'Always show', value: '' },
                  ...value.filter((_, n) => n !== i).filter((o) => o.questionCode)
                    .map((o) => ({ label: o.questionCode, value: o.questionCode }))]}
                optionLabel="label" optionValue="value" className="w-full"
                onChange={(e: { value: string }) => patch(i, {
                  visibleWhen: e.value ? { questionCode: e.value, equals: q.visibleWhen?.equals ?? [] } : null,
                })} />
            </FormField>

            {q.visibleWhen?.questionCode && (
              <FormField className="col-span-12 sm:col-span-6" label="…equals">
                <Input name={`qeq-${i}`} value={(q.visibleWhen.equals ?? []).join(', ')} className="w-full"
                  placeholder="1, 2, 3"
                  onChange={(v: string) => patch(i, {
                    visibleWhen: {
                      questionCode: q.visibleWhen!.questionCode,
                      equals: v.split(',').map((s) => s.trim()).filter(Boolean)
                        .map((s) => (/^-?\d+(\.\d+)?$/.test(s) ? Number(s) : s)),
                    },
                  })} />
              </FormField>
            )}
          </div>
        </div>
      ))}

      <div>
        <Button label="Add question" icon="pi pi-plus" outlined
          onClick={() => onChange([...value, blankQuestion(value.length + 1)])} />
      </div>
    </div>
  );
};

export default QuestionBuilder;
